import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { IncidentSafetyEpisode } from '../../services/incidentsApi';
import {
  resolveSafetyEpisodeCopy,
  viewMomentUnavailableCopy,
} from './incidentSafetyEpisodeCopy';

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatBoundaryReason(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  return raw.replace(/_/g, ' ');
}

function collectEvidenceRefs(episode: IncidentSafetyEpisode): string[] {
  const refs: string[] = [];
  if (episode.evidence_id) refs.push(`evidence: ${episode.evidence_id}`);
  if (episode.snapshot_id) refs.push(`snapshot: ${episode.snapshot_id}`);
  if (episode.clip_id) refs.push(`clip: ${episode.clip_id}`);
  return refs;
}

function resolveConfidence(episode: IncidentSafetyEpisode): number | null {
  const raw =
    episode.confidence
    ?? (episode.metadata && typeof episode.metadata === 'object'
      ? episode.metadata.confidence
      : null);
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Display model scores that may be 0–1 or 0–100. */
function formatConfidence(value: number | null): string {
  if (value === null) return '—';
  const pct = value <= 1.0 ? value * 100.0 : value;
  return `${Math.round(pct)}%`;
}

function episodeMeta(episode: IncidentSafetyEpisode): Record<string, any> | null {
  return episode.metadata && typeof episode.metadata === 'object'
    ? episode.metadata
    : null;
}

export type SafetyEpisodeEvidenceMoment = {
  clipId: string;
  offsetMs: number;
};

export type IncidentSafetyEpisodeTimelineProps = {
  episodes: IncidentSafetyEpisode[];
  loading: boolean;
  error: string | null;
  available: boolean | null;
  incidentType?: string | null;
  /** Falls back to `edgentik:view-evidence-moment` on window when omitted. */
  onViewMoment?: (moment: SafetyEpisodeEvidenceMoment) => void;
};

const IncidentSafetyEpisodeTimeline: React.FC<IncidentSafetyEpisodeTimelineProps> = ({
  episodes,
  loading,
  error,
  available,
  incidentType,
  onViewMoment,
}) => {
  const episodeFamily = episodes.find((episode) => episode.family)?.family;
  const copy = resolveSafetyEpisodeCopy(incidentType, episodeFamily);
  const unavailable = available === false;
  const hasLegacyUnindexed = episodes.some((episode) => {
    const meta = episodeMeta(episode);
    return (episode.evidence_status ?? meta?.evidence_status ?? 'legacy_unindexed')
      === 'legacy_unindexed';
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }} data-testid="safety-episode-timeline">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {copy.timelineHeading}
      </Typography>
      {!unavailable && !error && hasLegacyUnindexed ? (
        <Alert severity="info" data-testid="safety-episode-legacy-evidence-warning" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {copy.legacyEvidenceWarningTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {copy.legacyEvidenceWarningBody}
          </Typography>
        </Alert>
      ) : null}

      {unavailable ? (
        <Alert severity="info" data-testid="safety-episode-timeline-unavailable">
          {copy.timelineUnavailable}
        </Alert>
      ) : error ? (
        <Alert severity="warning" data-testid="safety-episode-timeline-error">
          {copy.timelineError}
        </Alert>
      ) : loading && episodes.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            {copy.timelineLoading}
          </Typography>
        </Box>
      ) : episodes.length === 0 ? (
        <Alert severity="info" data-testid="safety-episode-timeline-empty">
          {copy.timelineEmpty}
        </Alert>
      ) : (
        episodes.map((episode) => {
          const evidenceRefs = collectEvidenceRefs(episode);
          const personLabel = String(episode.local_track_id || '').trim() || episode.episode_id;
          const meta = episodeMeta(episode);
          const forkliftPresent =
            episode.forklift_present === true
            || meta?.forklift_present === true;
          const nearbyFromMeta = meta?.nearby_forklift_track_ids;
          const nearbyIds = Array.isArray(episode.nearby_forklift_track_ids)
            ? episode.nearby_forklift_track_ids
            : Array.isArray(nearbyFromMeta)
              ? nearbyFromMeta
              : [];
          const confidence = resolveConfidence(episode);
          const evidenceStatus = String(
            episode.evidence_status ?? meta?.evidence_status ?? 'legacy_unindexed',
          );
          const clipId = String(episode.clip_id ?? meta?.clip_id ?? '').trim();
          const rawOffset = episode.clip_offset_ms ?? meta?.clip_offset_ms;
          const hasOffset = rawOffset !== null && rawOffset !== undefined && rawOffset !== '';
          const offsetMs = hasOffset ? Number(rawOffset) : Number.NaN;
          const canViewMoment = evidenceStatus === 'indexed'
            && Boolean(clipId)
            && hasOffset
            && Number.isFinite(offsetMs)
            && offsetMs >= 0;
          const viewMoment = () => {
            if (!canViewMoment) return;
            const moment = { clipId, offsetMs };
            if (onViewMoment) {
              onViewMoment(moment);
            } else {
              window.dispatchEvent(new CustomEvent('edgentik:view-evidence-moment', {
                detail: moment,
              }));
            }
          };
          const forkliftSummary = forkliftPresent
            ? (nearbyIds.length > 0
              ? `co-present same camera (${nearbyIds.length})`
              : 'co-present same camera')
            : 'no forklift co-presence';

          return (
            <Accordion
              key={episode.episode_id}
              disableGutters
              elevation={0}
              defaultExpanded={false}
              TransitionProps={{ unmountOnExit: true }}
              data-testid={`safety-episode-row-${episode.episode_id}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                bgcolor: '#fcfcfd',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={{
                  minHeight: 44,
                  px: 1.25,
                  '& .MuiAccordionSummary-content': {
                    my: 0.75,
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {copy.localTrackId}: {personLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {copy.confidence}: {formatConfidence(confidence)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {forkliftSummary}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {episode.status || '—'}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={evidenceStatus.replace(/_/g, ' ')}
                  data-testid={`safety-episode-evidence-status-${episode.episode_id}`}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.confidence}: {formatConfidence(confidence)}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.startedAt}: {formatTimestamp(episode.started_at)}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.qualifiedAt}: {formatTimestamp(episode.qualified_at)}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.endedAt}: {formatTimestamp(episode.ended_at)}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.boundaryReason}: {formatBoundaryReason(episode.boundary_reason)}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.status}: {episode.status || '—'}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {copy.forkliftNearby}:{' '}
                  {forkliftPresent
                    ? nearbyIds.length > 0
                      ? `yes (${nearbyIds.join(', ')})`
                      : 'yes'
                    : 'no'}
                </Typography>
                {evidenceRefs.length > 0 ? (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{
                      display: 'block',
                      mt: 0.25,
                      wordBreak: 'break-all',
                    }}
                  >
                    {copy.evidenceRefs}: {evidenceRefs.join(' · ')}
                  </Typography>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canViewMoment}
                  onClick={viewMoment}
                  data-testid={`safety-episode-view-moment-${episode.episode_id}`}
                  sx={{ mt: 1 }}
                >
                  View moment
                </Button>
                {!canViewMoment ? (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    data-testid={`safety-episode-view-moment-reason-${episode.episode_id}`}
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {viewMomentUnavailableCopy(evidenceStatus)
                      || 'View moment is unavailable for this entry.'}
                  </Typography>
                ) : null}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};

export default IncidentSafetyEpisodeTimeline;
